import { useState } from 'react';

import { Square, SquareCheckBig } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { YES_NO_BASIC_OPTIONS } from '@/constants';
import { useQualityProgressionContext } from '@/contexts/QualityContext';
import useCognitoUsers from '@/hooks/useCognitoUsers';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { useQualityCheckActionPlans } from '@/hooks/useQualitiesAPI';
import { getUserInfo } from '@/lib/auth';

const QualityProgressionPlanPanel = () => {
  const [checkedCompleted, setCheckedCompleted] = useState();

  const { form, setFormByKey } = useQualityProgressionContext();
  const { actionPlansCompleted, actionPlansJustification, status } = form;
  const isQa = getUserInfo()?.['cognito:groups']?.includes('QA');
  const actionPlansConfirmed = Boolean(actionPlansCompleted);
  const planSubmitted =
    form.planSubmitted || actionPlansConfirmed || status === 'CLOSED';
  const canCheckActionPlans = status === 'PENDING_ACTION';
  const canSubmitActionPlans =
    isQa &&
    canCheckActionPlans &&
    checkedCompleted !== undefined &&
    !planSubmitted &&
    (checkedCompleted === 0 ||
      (checkedCompleted === 1 &&
        Boolean(actionPlansJustification?.trim())));
  const { getBySub } = useCognitoUsers();
  const { checkActionPlansMutation } = useQualityCheckActionPlans();

  const { showConfirmModal } = useConfirmModal();

  const confirmSubmit = async () => {
    if (!isQa || !canCheckActionPlans) return;

    await checkActionPlansMutation(form.id, {
      completed: checkedCompleted === 1,
      justification: actionPlansJustification,
    });
    setFormByKey('planSubmitted', true);
    if (checkedCompleted === 1) {
      setFormByKey('actionPlansCompleted', true);
      setFormByKey('status', form.planRequired ? 'PENDING_EC' : 'CLOSED');
    } else {
      setFormByKey('status', 'CLOSED');
    }
  };

  const handleSubmit = () => {
    const nextStatus = checkedCompleted === 1
      ? (form.planRequired ? 'Pending EC' : 'Closed')
      : 'Closed';
    showConfirmModal({
      title: 'Confirm Action Completion',
      description: `You are confirming the action plan implementation as ${checkedCompleted === 1 ? 'complete' : 'not complete'}. Once confirmed, the Quality Event status will advance to ${nextStatus} and you cannot edit this section anymore.`,
      onConfirm: confirmSubmit,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Follow Up on Action Plan Implementation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">S/N</TableHead>
                <TableHead>
                  Action Plan / Corrective Action Description
                </TableHead>
                <TableHead className="w-48">Responsible Person</TableHead>
                <TableHead className="w-56">
                  Target Implementation Date
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {form.planActions?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{getBySub(item.account)?.name}</TableCell>
                  <TableCell>{item.implementAt}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        <Card>
          <CardHeader className="p-4 grow">
            <div className="flex gap-4 text-sm">
              <div className="grow content-center">
                Have all action plans been completed?
              </div>
              <div className="basis-40">
                <Select
                  disabled={!isQa || !canCheckActionPlans || planSubmitted}
                  onChange={({ target }) =>
                    setCheckedCompleted(Number(target.value))
                  }
                  value={actionPlansConfirmed ? 1 : (planSubmitted ? 0 : checkedCompleted)}
                >
                  {!actionPlansConfirmed && !planSubmitted && checkedCompleted === undefined && (
                    <option value="">-</option>
                  )}
                  {YES_NO_BASIC_OPTIONS.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="p-4 grow">
            <div className="flex gap-4 text-sm">
              <div className="grow content-center">
                Describe Evidence / Justification
              </div>
            </div>
          </CardHeader>
          <CardContent className="pb-4 px-4">
            <Textarea
              autoResize
              className="font-mono resize-none"
              disabled={
                !isQa ||
                !canCheckActionPlans ||
                planSubmitted
              }
              onChange={({ target }) =>
                setFormByKey('actionPlansJustification', target.value)
              }
              placeholder="Enter evidence details..."
              value={actionPlansJustification || ''}
            />
          </CardContent>
        </Card>
        {isQa && (
          <div className="text-right">
            <Button disabled={!canSubmitActionPlans} onClick={handleSubmit}>
              {actionPlansConfirmed ? <SquareCheckBig /> : <Square />}
              {checkedCompleted === 0 ? 'Submit' : 'Action Plan Completed'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QualityProgressionPlanPanel;
